import React from "react";
import { isMobile } from "react-device-detect";
import { Trans, useTranslation } from "react-i18next";

import {
  Button,
  List,
  ListItem,
  Spacing,
  Stack,
} from "@noom/wax-component-library";

import {
  QuestionComponentProps,
  QuestionDefinition,
} from "@/components/survey/Question";
import { NoomMedOverviewSelection, ProgramOffering } from "@/constants";
import { useAppContext } from "@/contexts";
import { useTrackEvent } from "@/hooks";

import { BaseQuestion } from "../core";

const QUESTION_ID_DIABETES = "confirmChangeProgramDiabetes";
const QUESTION_ID_DPP = "confirmChangeProgramDpp";
const QUESTION_ID_MED = "confirmChangeProgramMed";

declare module "@/contexts" {
  interface SurveyAnswers {
    [QUESTION_ID_DIABETES]?: boolean;
    [QUESTION_ID_DPP]?: boolean;
    [QUESTION_ID_MED]?: boolean;
  }
}

type ConfirmChangeProgramProps = QuestionComponentProps & {
  questionId: string;
};

const ConfirmChangeProgram: React.FC<ConfirmChangeProgramProps> = ({
  onClickBack,
  onClickNext,
  questionId,
}) => {
  const { qualifiedPrograms, retriageEligibility, setSelectedProgram } =
    useAppContext();
  const { t } = useTranslation();
  const { trackAnonEvent } = useTrackEvent();

  const getProgramFromQuestionId = (): ProgramOffering => {
    if (questionId === QUESTION_ID_MED) return "MED";
    if (questionId === QUESTION_ID_DIABETES) return "DIABETES_LIFESTYLE";
    return "DPP";
  };
  const newProgram = getProgramFromQuestionId();
  const programKey = newProgram.toLowerCase();
  const hasMoreOptions =
    retriageEligibility?.currentProgram === "DPP" &&
    qualifiedPrograms.includes("DIABETES_LIFESTYLE") &&
    newProgram !== "DIABETES_LIFESTYLE";

  const primaryCtaText = t(`confirmChangeProgram.continue.${programKey}`);
  const secondaryCtaText = hasMoreOptions
    ? t("confirmChangeProgram.moreOptions")
    : t("confirmChangeProgram.cancel");

  const medValueProps = (
    <List>
      <ListItem marginLeft="20px">
        {t("confirmChangeProgram.valueProp1")}
      </ListItem>
      <ListItem marginLeft="20px">
        {t("confirmChangeProgram.valueProp2")}
      </ListItem>
    </List>
  );

  const imageProps = isMobile
    ? {
        headerImageSrc: `/assets/img/app-preview-${programKey}-mobile.svg`,
      }
    : {
        imageSrc: `/assets/img/app-preview-${programKey}-desktop.svg`,
      };

  const handleOptIn = (didOptIn: boolean) => {
    const updatedProgram = didOptIn ? newProgram : null;

    setSelectedProgram(updatedProgram);
    onClickNext(didOptIn, { updatedProgram });
  };

  const handleClickPrimaryCta = () => {
    trackAnonEvent("b2bEnrollmentAnonButtonClicked", {
      buttonName: primaryCtaText,
      pageId: questionId,
    });

    handleOptIn(true);
  };

  const handleClickSecondaryCta = () => {
    trackAnonEvent("b2bEnrollmentAnonButtonClicked", {
      buttonName: secondaryCtaText,
      pageId: questionId,
    });

    if (hasMoreOptions) {
      onClickNext(false, {
        noomMedOverview: NoomMedOverviewSelection.SEE_MORE_OPTIONS,
      });
    } else {
      handleOptIn(false);
    }
  };

  return (
    <BaseQuestion
      {...imageProps}
      hideSurveyProgressBar
      onClickBack={onClickBack}
      questionId={questionId}
      questionText={t(`confirmChangeProgram.title.${programKey}`)}
    >
      <Stack spacing={Spacing[8]}>
        <Trans
          components={{ medValueProps }}
          i18nKey={`confirmChangeProgram.prompt.${programKey}`}
        />
        <Stack spacing={Spacing[4]} pt={Spacing[2]}>
          <Button
            colorScheme="primary"
            onClick={handleClickPrimaryCta}
            size="xl"
            type="submit"
          >
            {primaryCtaText}
          </Button>
          <Button onClick={handleClickSecondaryCta} size="xl" variant="outline">
            {secondaryCtaText}
          </Button>
        </Stack>
      </Stack>
    </BaseQuestion>
  );
};

const ConfirmChangeProgramDiabetes: React.FC<QuestionComponentProps> = (
  props,
) => <ConfirmChangeProgram {...props} questionId={QUESTION_ID_DIABETES} />;

const ConfirmChangeProgramDpp: React.FC<QuestionComponentProps> = (props) => (
  <ConfirmChangeProgram {...props} questionId={QUESTION_ID_DPP} />
);

const ConfirmChangeProgramMed: React.FC<QuestionComponentProps> = (props) => (
  <ConfirmChangeProgram {...props} questionId={QUESTION_ID_MED} />
);

export const Q_CONFIRM_CHANGE_PROGRAM_DPP: QuestionDefinition = {
  id: QUESTION_ID_DPP,
  supportedPrograms: ["DPP"],
  shouldShowQuestion: () => true,
  component: ConfirmChangeProgramDpp,
};

export const Q_CONFIRM_CHANGE_PROGRAM_MED: QuestionDefinition = {
  id: QUESTION_ID_MED,
  supportedPrograms: ["MED", "NMPBA"],
  shouldShowQuestion: ({
    retriageEligibility,
    surveyAnswers: { noomMedOverview },
  }) =>
    noomMedOverview === NoomMedOverviewSelection.NEXT &&
    retriageEligibility?.currentProgram === "DPP",
  component: ConfirmChangeProgramMed,
};

export const Q_CONFIRM_CHANGE_PROGRAM_DIABETES: QuestionDefinition = {
  id: QUESTION_ID_DIABETES,
  supportedPrograms: ["DIABETES_LIFESTYLE"],
  shouldShowQuestion: ({ retriageEligibility }) =>
    retriageEligibility?.currentProgram === "DPP",
  component: ConfirmChangeProgramDiabetes,
};
